import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EmptyList,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { GET_QUERY_KEY, QUERY_OPTIONS } from "../../common/constants";
import { IResponse, IServerError } from "../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	formatDate,
	formatDateToShow,
	sortDDLByLabel,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount,
} from "../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { ClientService } from "../../services";
import { EmployeeService } from "../../services/EmployeeService";
import { IAppState } from "../../store/reducers";
import { IDropDownListResponse } from "../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../models";
import { LookupFieldService } from "../../services/LookupFieldService";
import { PayrollScheduleDataGrid } from "./PayrollScheduleDataGrid";
import { PayrollScheduleHeader } from "./PayrollScheduleHeader";
import { PayrollScheduleService } from "../../services/PayrollScheduleService";
import { PayrollScheduleValidationSchema } from "./validator";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { yupResolver } from "@hookform/resolvers/yup";

const payrollScheduleHeaderColumns = [
	{
		field: "startDate",
		headerName: "Start Date",
		flex: 1,
		hide: false,
		hideable: false,
		sortable: true,
		sortingOrder: "asc",
		resizable: false,
		type: "string",
		align: "center",
		headerAlign: "left",
		hideSortIcons: false,
		disableColumnMenu: true,
		disableReorder: true,
		disableExport: true,
		maskable: false,
		isFixed: true,
		sequence: 0,
		frozenColumn: false,
		footer: null,
	},
	{
		field: "endDate",
		headerName: "End Date",
		flex: 1,
		hide: false,
		hideable: false,
		sortable: true,
		sortingOrder: "asc",
		resizable: false,
		type: "string",
		align: "center",
		headerAlign: "left",
		hideSortIcons: false,
		disableColumnMenu: true,
		disableReorder: true,
		disableExport: true,
		maskable: false,
		isFixed: true,
		sequence: 0,
		frozenColumn: false,
		footer: null,
	},
	{
		field: "days",
		headerName: "Days",
		flex: 1,
		hide: false,
		hideable: false,
		sortable: true,
		sortingOrder: "asc",
		resizable: false,
		type: "string",
		align: "center",
		headerAlign: "left",
		hideSortIcons: false,
		disableColumnMenu: true,
		disableReorder: true,
		disableExport: true,
		maskable: false,
		isFixed: true,
		sequence: 0,
		frozenColumn: false,
		footer: null,
	},
	{
		field: "paymentDate",
		headerName: "Payment Date",
		flex: 1,
		hide: false,
		hideable: false,
		sortable: true,
		sortingOrder: "asc",
		resizable: false,
		type: "string",
		align: "center",
		headerAlign: "left",
		hideSortIcons: false,
		disableColumnMenu: true,
		disableReorder: true,
		disableExport: true,
		maskable: false,
		isFixed: true,
		sequence: 0,
		frozenColumn: false,
		footer: null,
	},
	{
		field: "payrollProcessingDate",
		headerName: "Payroll Processing Date",
		flex: 1,
		hide: false,
		hideable: false,
		sortable: true,
		sortingOrder: "asc",
		resizable: false,
		type: "string",
		align: "center",
		headerAlign: "left",
		hideSortIcons: false,
		disableColumnMenu: true,
		disableReorder: true,
		disableExport: true,
		maskable: false,
		isFixed: true,
		sequence: 0,
		frozenColumn: false,
		footer: null,
	},
];
export interface IPayrollSchedule { }
// const PAYROLL_SCHEDULE_LIST_QUERY_KEY = "payroll-schedule";
const SINGLE_RECORD_DATA_QUERY_KEY = "payroll-schedule-single-data-record";

const useGetData = (
	clientId: string,
	id: string | undefined,
	selectedYear: number,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, id, selectedYear, navigateToLogout],
		queryFn: () => {
			return PayrollScheduleService.getById(clientId, id, selectedYear, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

// const usePayrollSchedule = (
// 	clientId: string,
// 	params: IGetListRequest,
// 	navigateToLogout: (path: string) => void
// ) =>
// 	useQuery({
// 		queryKey: [PAYROLL_SCHEDULE_LIST_QUERY_KEY, params],
// 		queryFn: (data) => {

// 			return PayrollScheduleService.getPayrollSchedule(
// 				clientId,
// 				params,
// 				navigateToLogout
// 			);
// 		},
// 		refetchOnWindowFocus: false,
// 	});

export const useGetPayrollScheduleYearsDDL = (
	clientId: string,
	taxYear: number
) =>
	useQuery({
		queryKey: ["years-ddl"],
		queryFn: (data) => {
			return PayrollScheduleService.getYearsDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

export const useGetPayrollFrequenciesDDL = () =>
	useQuery({
		queryKey: ["payroll-frequencies-ddl"],
		queryFn: (data) => {
			return PayrollScheduleService.getPayrollFrequenciesDDL();
		},
		refetchOnWindowFocus: false,
	});

export const useGetPayPeriodLabelsDDL = (clientId: string, selectedPayPeriodId: string | null) =>
	useQuery({
		queryKey: ["pay-period-labels-ddl", selectedPayPeriodId],
		queryFn: (data) => {
			return PayrollScheduleService.getPayPeriodLabelsDDL(clientId, selectedPayPeriodId);
		},
		refetchOnWindowFocus: false,
	});

export const useGetPayrollSchedulePermissions = (clientId: string, selectedPayPeriodId: string | undefined) =>
	useQuery({
		queryKey: ["pay-period-labels-ddl", selectedPayPeriodId],
		queryFn: (data) => {
			return PayrollScheduleService.getPayrollSchedulePermissions(clientId, selectedPayPeriodId);
		},
		refetchOnWindowFocus: false,
	});

const CLIENT_INFO_DATA_QUERY_KEY = "client-info-data-record";
const useGetClientInformationById = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [CLIENT_INFO_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: (data) => {
			return ClientService.getSimpleClientById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

export const PayrollScheduleSetup: React.FC<IPayrollSchedule> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	// const companyScheduleStartDate = useSelector((state: IAppState) => state.dashboardApisData.client_detail.scheduleStartDate);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	// for delete row Purpose
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [selectedPayPeriodId, setSelectedPayPeriodId] =
		useState(null);
	const [payrollScheduleData, setPayrollScheduleData] =
		useState<any>(undefined);
	const [openDialog, setDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [payrollScheduleId, setPayrollScheduleId] = useState<number | string>(
		0
	);
	const [selectedYear, setSelectedYear] = useState<number>(
		taxYear
	);

	const { isLoading: isLoadingTaxYearDDL, data: yearDDL } =
		useGetPayrollScheduleYearsDDL(clientId, taxYear);

	const updatedYearDDL = yearDDL && yearDDL.map((item: any) => ({
		...item,
		label: String(item.label)
	}));


	const { isPending: isPendingClientInformationById, data: companyInformation } = useGetClientInformationById(
		clientId,
		navigateToLogout
	);

	const { isLoading: isLoadingPayrollFrequenciesDDL, data: payrollFrequenciesDDL } =
		useGetPayrollFrequenciesDDL();
	const { isLoading: isLoadingPayPeriodLabelsDDL, data: payPeriodLabelsDDL } = useGetPayPeriodLabelsDDL(
		clientId,
		selectedPayPeriodId
	);


	const { isLoading: isLoadingPayrollSchedulePermissions, data: payrollSchedulePermissions } = useGetPayrollSchedulePermissions(
		clientId,
		editRecordId
	);

	const DEFAULT_VALUES = {
		year: undefined,
		payPeriodLabelList: undefined,
		firstPayPeriodEndDate: null,
		setPaymentDate: 0,
		showInPayroll: 200,
		emailAlert: 200,
	};
	const formMethods = useForm<any>({
		resolver: yupResolver(PayrollScheduleValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});

	const { isPending: isSubmitting, mutate: submitPayrollScheduleFormFields } =
		useMutation({
			mutationFn: PayrollScheduleService.create,
			onSuccess: (res: IPayrollSchedule | IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIPayrollScheduleResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/company/payroll-schedule/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: ["pay-period-labels-ddl"],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {
					// if (instanceOfIPayrollScheduleResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/company/payroll-schedule/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: ["pay-period-labels-ddl"],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		clientId,
		editRecordId,
		selectedYear,
		navigateToLogout
	);

	useEffect(() => {
		if (selectedDataRecord) {
			setPayrollScheduleData(selectedDataRecord);
		}
	}, [selectedDataRecord]);

	useEffect(() => {
		if (payrollScheduleData) {
			Object.keys(payrollScheduleData).forEach((fieldName) => {
				const fieldValue = payrollScheduleData[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "firstPayPeriodEndDate") {
					const defaultDate = new Date(fieldValue);
					formMethods.setValue(fieldName, defaultDate);
				}
				if (fieldName === "id") {
					formMethods.setValue("payPeriodLabelList", fieldValue);

				}
				if (fieldName === "payPeriodId") {
					formMethods.setValue("payPeriod", fieldValue);
				}
			});
		}
	}, [payrollScheduleData]);

	const onSubmit = (values: any) => {
		const selectedPayPeriod = payrollFrequenciesDDL && payrollFrequenciesDDL.find((x: any) => x.value === values.payPeriod);
		// const selectedPayPeriodByPayrollScheduleData = payrollFrequenciesDDL && payrollScheduleData && payrollFrequenciesDDL.find((x: any) => x.code === Number(values.payPeriod));
		const selectedPayPeriodByPayrollScheduleData = payrollFrequenciesDDL && payrollScheduleData && payrollFrequenciesDDL.find((x: any) => x.value === values.payPeriod);

		// const companyScheduleStartDateYear = companyScheduleStartDate && new Date(companyScheduleStartDate).getFullYear()
		// const companyScheduleStartDateMonth = companyScheduleStartDate && new Date(companyScheduleStartDate).getMonth() + 1
		const firstPayPeriodEndDateYear = new Date(values.firstPayPeriodEndDate).getFullYear()
		// const firstPayPeriodEndDateMonth = new Date(values.firstPayPeriodEndDate).getMonth() + 1

		// Convert to Date objects
		const firstPayEndDate = new Date(values.firstPayPeriodEndDate);
		const companyStartDate = companyInformation && companyInformation.scheduleStartDate && new Date(companyInformation.scheduleStartDate);

		// Normalize time to midnight (ignore hours, minutes, seconds)
		firstPayEndDate.setHours(0, 0, 0, 0);
		companyStartDate.setHours(0, 0, 0, 0);

		if (values.year !== firstPayPeriodEndDateYear) {
			setSnackbarData({
				open: true,
				message: "First pay period end date must be with in current year of operation.",
				severity: "error",
			});
		} else if (firstPayEndDate < companyStartDate) {
			setSnackbarData({
				open: true,
				message: `Pay period end date should be later than company start date, ${companyInformation && companyInformation.scheduleStartDate && formatDateToShow(companyInformation.scheduleStartDate)}`,
				severity: "error",
			});
		} else {
			let payload = {
				id: id && id,
				clientId: clientId,
				taxYear: values.year,
				payPeriodId: payrollScheduleData ? selectedPayPeriodByPayrollScheduleData.value : values.payPeriod,
				payPeriod: payrollScheduleData ? selectedPayPeriodByPayrollScheduleData.code : selectedPayPeriod.code,
				firstPayPeriodEndDate: formatDate(values.firstPayPeriodEndDate),
				setPaymentDate: values.setPaymentDate,
				showInPayroll: values.showInPayroll,
				payPeriodLabel: values.payPeriodLabel,
				emailAlert: values.emailAlert,
				weakStart: values.weakStart,
			};

			submitPayrollScheduleFormFields(payload);
		}

	};

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "firstName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	// const YearDDlItems = useMemo(() => {
	// 	if (yearDDL) {
	// 		if (
	// 			yearDDL &&
	// 			Array.isArray(yearDDL)
	// 		) {
	// 			let resources: IDropDownListResponse[] =
	// 				yearDDL.map(
	// 					(resourceItem: IDropDownListResponse) => ({
	// 						// id: resourceItem.id,
	// 						label: resourceItem.label,
	// 						value: resourceItem.label,
	// 					})
	// 				);
	// 			return resources;
	// 		}
	// 		return [];
	// 	}
	// }, [yearDDL]);

	const PayPeriodLabelsDDlItems: IDropDownListResponse[] | undefined =
		useMemo(() => {
			if (payPeriodLabelsDDL) {
				if (payPeriodLabelsDDL && Array.isArray(payPeriodLabelsDDL)) {
					let resources: IDropDownListResponse[] = sortDDLByLabel(payPeriodLabelsDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

					resources.unshift({
						label: "Create New",
						value: "create-new",
					});

					return resources;
				}
				return [];
			}
		}, [payPeriodLabelsDDL]);

	const onPayPeriodLabelChangeHandler = (selected: any) => {
		let value = selected.value;
		let label = selected.label;
		if (value === "create-new") {
			setPayrollScheduleData(undefined);
			formMethods.setValue("payPeriodLabel", undefined);
			formMethods.setValue("firstPayPeriodEndDate", null);
			formMethods.setValue("setPaymentDate", 0);
			formMethods.setValue("showInPayroll", 200);
			formMethods.setValue("emailAlert", 200);
			let path = `/company/payroll-schedule/`;
			navigate(path);
			setEditRecordId(undefined);
		} else {
			formMethods.setValue("payPeriodLabel", label);
			let path = `/company/payroll-schedule/${value}`;
			navigate(path);
			setEditRecordId(value);
		}
	};
	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deletePayrollScheduleById } =
		useMutation({
			mutationFn: PayrollScheduleService.delPayrollSchedule,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the PayrollSchedule fields since we just added a new field.
					queryClient.invalidateQueries({
						queryKey: ["pay-period-labels-ddl"],
					});
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				setPayrollScheduleData(undefined);
				setEditRecordId(undefined);
				let path = `/company/payroll-schedule/`;
				navigate(path);

			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deletePayrollScheduleField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				payrollScheduleId: payrollScheduleId,
				navigateToLogout: navigateToLogout,
			};
			deletePayrollScheduleById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setPayrollScheduleId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const onYearyChangeHandler = (selectedYear: any) => {
		let value = selectedYear.value

		setSelectedYear(value)
	}

	const expandedLabels = ["Company"];
	const expandedChild = "Payroll Schedule"; // Special case for "List" under "Employee"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === "Comapny" && item.label === expandedChild) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedChild && "/company/payroll-schedule",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);

	return (
		<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
			<Grid size={{ xs: 12, sm: 12, md: 8.5, lg: 8.5, xl: 8 }}>
				<FormProvider {...formMethods}>
					<form onSubmit={formMethods.handleSubmit(onSubmit)}>
						<PayrollScheduleHeader
							// YearDDlItems={YearDDlItems}
							YearDDlItems={yearDDL && sortDDLByLabel(updatedYearDDL)}
							taxYear={taxYear}
							PayPeriodLabelsDDlItems={PayPeriodLabelsDDlItems && PayPeriodLabelsDDlItems}
							onPayPeriodLabelChangeHandler={onPayPeriodLabelChangeHandler}
							payrollScheduleData={payrollScheduleData && payrollScheduleData}
							payrollFrequenciesDDL={payrollFrequenciesDDL && sortDDLByLabel(payrollFrequenciesDDL)}
							onDeleteClicked={onDeleteClicked}
							selectedPayPeriodId={(value: any) => setSelectedPayPeriodId(value)}
							onYearyChangeHandler={onYearyChangeHandler}
							id={id && id}
							payrollSchedulePermissions={payrollSchedulePermissions && payrollSchedulePermissions}
							formMethods={formMethods}
							setEditRecordId={setEditRecordId}
							setPayrollScheduleData={(value: any) => setPayrollScheduleData(value)}
						/>
					</form>
				</FormProvider>

				<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
					<BackdropCircularProgress
						open={
							isPendingById || isSubmitting || isDeleting || isLoadingTaxYearDDL || isLoadingPayrollFrequenciesDDL || isLoadingPayPeriodLabelsDDL
							|| isLoadingPayrollSchedulePermissions || isPendingClientInformationById
						}
						testId="T4-report"
					/>
					<DeleteConfirmationDialog
						open={openDialog}
						deleteActionHandler={deletePayrollScheduleField}
						cancelActionHandler={onCloseHandler}
						testId="departments-confirmation-dialog"
					/>
					<PayrollScheduleDataGrid
						columns={
							payrollScheduleData && payrollScheduleData.data
								? payrollScheduleHeaderColumns
								: []
						}
						rows={
							payrollScheduleData && payrollScheduleData.data
								? payrollScheduleData.data
								: []
						}
					/>
				</Grid>

				<EnhancedSnackbar
					message={snackbarData.message}
					onCloseHandler={onSnackbarClosed}
					severity={snackbarData.severity}
					testId={`snackbar`}
					open={snackbarData.open}
				/>
			</Grid>
		</Grid>
	);
};
